import { ACTIVE_EXPERIMENTS, type ExperimentRegistry } from './experiments';
import { safeGetStorageItem, safeRemoveStorageItem, safeSetStorageItem } from './browserStorage';
import { isPreviewExperimentOverrideHost } from './experimentOverrideHosts';

export const EXPERIMENT_PREVIEW_STORAGE_KEY = 'botspot:experiment-preview-overrides:v1';
export const EXPERIMENT_PREVIEW_OVERRIDE_EVENT = 'botspot:experiment-preview-overrides-change';

export type ExperimentPreviewEntry = {
  registryKey: string;
  label: string;
  route: string;
  description: string;
  experimentName: string;
  featureFlag: string;
  posthogExperimentId?: number;
  status: string;
  variants: { label: string; value: string }[];
};

/**
 * Settings > Experiments lists every registry experiment that opts in with a
 * `preview` entry and has a flag and variants. The list comes from the
 * registry so an ended experiment leaves the preview page when its registry
 * record is removed (the pricing page test, ended 2026-10-22, was the last one).
 */
export const buildExperimentPreviewCatalog = (
  registry: ExperimentRegistry,
): ExperimentPreviewEntry[] =>
  Object.entries(registry).flatMap(([registryKey, experiment]) => {
    if (!experiment.preview || !experiment.featureFlag || !experiment.variants) return [];
    return [
      {
        registryKey,
        label: experiment.preview.label,
        route: experiment.preview.route,
        description: experiment.description,
        experimentName: experiment.name,
        featureFlag: experiment.featureFlag,
        posthogExperimentId: experiment.posthog_experiment_id,
        status: experiment.status,
        variants: Object.entries(experiment.variants).map(([value, label]) => ({ label, value })),
      },
    ];
  });

export const EXPERIMENT_PREVIEW_CATALOG = buildExperimentPreviewCatalog(ACTIVE_EXPERIMENTS);

type PreviewOverrides = Record<string, string>;

type PreviewOverrideEventDetail = {
  featureFlagKey?: string;
  variant?: string | null;
};

const getCurrentHostname = (): string => globalThis.window?.location?.hostname || '';

const parseStoredOverrides = (rawOverrides: string): PreviewOverrides => {
  const parsedOverrides = JSON.parse(rawOverrides);
  if (
    parsedOverrides === null ||
    Array.isArray(parsedOverrides) ||
    Object.prototype.toString.call(parsedOverrides) !== '[object Object]'
  ) {
    return {};
  }

  const candidateEntries = Object.entries(parsedOverrides);
  return EXPERIMENT_PREVIEW_CATALOG.reduce<PreviewOverrides>((overrides, experiment) => {
    const candidateVariant = candidateEntries.find(
      ([featureFlag]) => featureFlag === experiment.featureFlag,
    )?.[1];
    const storedVariant = experiment.variants.find(({ value }) => value === candidateVariant);
    if (storedVariant) overrides[experiment.featureFlag] = storedVariant.value;
    return overrides;
  }, {});
};

const readStoredOverrides = (): PreviewOverrides => {
  const rawOverrides = safeGetStorageItem('sessionStorage', EXPERIMENT_PREVIEW_STORAGE_KEY);
  if (!rawOverrides) return {};

  try {
    return parseStoredOverrides(rawOverrides);
  } catch {
    return {};
  }
};

const dispatchOverrideChange = (detail: PreviewOverrideEventDetail): void => {
  const browserWindow = globalThis.window;
  if (!browserWindow) return;
  browserWindow.dispatchEvent(
    new browserWindow.CustomEvent(EXPERIMENT_PREVIEW_OVERRIDE_EVENT, { detail }),
  );
};

const getPreviewExperiment = (featureFlagKey: string) =>
  EXPERIMENT_PREVIEW_CATALOG.find((experiment) => experiment.featureFlag === featureFlagKey);

export const isExperimentPreviewAvailable = (hostname = getCurrentHostname()): boolean =>
  isPreviewExperimentOverrideHost(hostname);

export const getExperimentPreviewOverride = (
  featureFlagKey: string,
  validVariants: readonly string[] = [],
  hostname = getCurrentHostname(),
): string | null => {
  if (
    !isExperimentPreviewAvailable(hostname) ||
    validVariants.length === 0 ||
    !getPreviewExperiment(featureFlagKey)
  ) {
    return null;
  }

  const storedOverrides = readStoredOverrides();
  const storedVariant = Object.prototype.hasOwnProperty.call(storedOverrides, featureFlagKey)
    ? storedOverrides[featureFlagKey]
    : null;

  return storedVariant && validVariants.includes(storedVariant) ? storedVariant : null;
};

export const setExperimentPreviewOverride = (
  featureFlagKey: string,
  variant: string | null,
  validVariants: readonly string[],
  hostname = getCurrentHostname(),
): boolean => {
  if (!isExperimentPreviewAvailable(hostname)) return false;
  const previewExperiment = getPreviewExperiment(featureFlagKey);
  if (!previewExperiment) return false;
  if (
    variant !== null &&
    (!validVariants.includes(variant) ||
      !previewExperiment.variants.some(({ value }) => value === variant))
  ) {
    return false;
  }

  const storedOverrides = readStoredOverrides();
  if (variant === null) {
    delete storedOverrides[featureFlagKey];
  } else {
    storedOverrides[featureFlagKey] = variant;
  }

  let didPersist = true;
  if (Object.keys(storedOverrides).length === 0) {
    safeRemoveStorageItem('sessionStorage', EXPERIMENT_PREVIEW_STORAGE_KEY);
  } else {
    didPersist = safeSetStorageItem(
      'sessionStorage',
      EXPERIMENT_PREVIEW_STORAGE_KEY,
      JSON.stringify(storedOverrides),
    );
  }

  if (!didPersist) return false;

  dispatchOverrideChange({ featureFlagKey, variant });
  return true;
};

export const clearExperimentPreviewOverrides = (hostname = getCurrentHostname()): boolean => {
  if (!isExperimentPreviewAvailable(hostname)) return false;

  safeRemoveStorageItem('sessionStorage', EXPERIMENT_PREVIEW_STORAGE_KEY);
  dispatchOverrideChange({});
  return true;
};
